import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * Thaheb changes on top of the theme version:
 * - opens at once when a product page add starts (thaheb:cart-adding) and shows an "adding" row until the real
 *   cart replaces it; a failed add (thaheb:cart-add-settled with ok:false) closes it again
 * - takes over history.scrollRestoration while the phone history entry is pushed and popped, so closing the
 *   drawer no longer jumps the page back to the top
 * - an empty cart at open keeps the footer pinned (see #updateStickyState)
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #savedScrollRestoration = null;

  /** True from the moment the drawer is asked to open until its open event fires (stops a second showDialog). */
  #opening = false;

  /** True while a product page add is in flight and the drawer shows the "adding" row. */
  #adding = false;

  /** True when the drawer was opened early by thaheb:cart-adding, so a failed add can close it again. */
  #openedEarly = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #addingTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('thaheb:cart-adding', this.#handleCartAdding);
    document.addEventListener('thaheb:cart-add-settled', this.#handleCartAddSettled);
    document.addEventListener('cart:error', this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpened);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClosed);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('thaheb:cart-adding', this.#handleCartAdding);
    document.removeEventListener('thaheb:cart-add-settled', this.#handleCartAddSettled);
    document.removeEventListener('cart:error', this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpened);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClosed);
    this.#historyAbortController?.abort();
    this.#restoreScrollRestoration();
    if (this.#addingTimer) clearTimeout(this.#addingTimer);
  }

  #handleOpened = () => {
    this.#opening = false;
  };

  #handleClosed = () => {
    this.#opening = false;
    this.#endAdding(false);
  };

  /**
   * The phone history entry is pushed while the page is scroll-locked (body fixed, scrollY 0), so the browser's
   * automatic scroll restoration sends the page to the top when that entry is popped. Take restoration over for
   * the lifetime of the entry; the theme already scrolls back to where the customer was when the dialog closes.
   */
  #setManualScrollRestoration() {
    if (!('scrollRestoration' in history)) return;
    if (this.#savedScrollRestoration === null) this.#savedScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
  }

  #restoreScrollRestoration() {
    if (this.#savedScrollRestoration === null) return;
    history.scrollRestoration = this.#savedScrollRestoration;
    this.#savedScrollRestoration = null;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      this.#setManualScrollRestoration();
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (!history.state?.cartDrawerOpen) {
      this.#restoreScrollRestoration();
      return;
    }

    // Closing with the X or the overlay: pop our entry, keep the page exactly where the customer was.
    const scrollY = window.scrollY;
    let settled = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let fallback;

    const settle = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('popstate', settle);
      clearTimeout(fallback);

      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollY) > 1) {
          window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
        }
        this.#restoreScrollRestoration();
      });
    };

    fallback = setTimeout(settle, 600);
    window.addEventListener('popstate', settle, { once: true });
    history.back();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !this.refs.dialog?.open && !this.#opening) {
      this.#markOpening();
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #markOpening() {
    this.#opening = true;
    // If the open event never arrives (dialog refused), do not stay stuck.
    setTimeout(() => {
      this.#opening = false;
    }, 1200);
  }

  /**
   * A product page add has started: open the drawer straight away and show an "adding" row at the top, so the
   * customer is not left waiting on the product page for the add and the cart render.
   * @param {Event} rawEvent
   */
  #handleCartAdding = (rawEvent) => {
    const event = /** @type {CustomEvent<{ sourceElement?: Element }>} */ (rawEvent);
    if (!this.hasAttribute('auto-open')) return;
    // One drawer answers, even if the header is rendered twice.
    if (document.querySelector('cart-drawer-component') !== this) return;

    // Let other listeners (a quick add modal, for example) claim the add first.
    queueMicrotask(() => {
      if (event.defaultPrevented) return;

      const dialog = this.refs.dialog;
      if (!dialog || dialog.open || this.#opening) return;
      // Another dialog is on screen: the normal flow opens the drawer once the add lands.
      if (document.querySelector('dialog[open]')) return;

      this.#adding = true;
      this.#cartErrorSeen = false;
      this.#openedEarly = true;
      this.#showAddingRow(event.detail?.sourceElement);
      this.#markOpening();
      this.showDialog();

      if (this.#addingTimer) clearTimeout(this.#addingTimer);
      // Failsafe: never leave the row on screen if no settle event arrives.
      this.#addingTimer = setTimeout(() => this.#endAdding(false), 12000);
    });
  };

  /** Set when the product form reported its own error (cart:error) just before an add settled as failed. */
  #cartErrorSeen = false;

  #handleCartError = () => {
    this.#cartErrorSeen = true;
  };

  /**
   * The add finished. On failure take the "adding" row away; the drawer closes again when the product form already
   * showed an error on the page, otherwise it stays open and says the bag could not be updated. On success the
   * cart render replaces the row; if it is somehow still there a moment later, ask for a fresh render.
   * @param {Event} rawEvent
   */
  #handleCartAddSettled = (rawEvent) => {
    const event = /** @type {CustomEvent<{ ok?: boolean }>} */ (rawEvent);
    if (!this.#adding) return;

    if (event.detail?.ok === false) {
      const reportedOnPage = this.#cartErrorSeen;
      this.#cartErrorSeen = false;
      if (reportedOnPage) {
        // The product form already shows its own message on the page, so the drawer just closes again.
        this.#endAdding(true);
      } else {
        // A busy store or a lost connection: stay open, drop the skeleton line and say what happened.
        this.#endAdding(false);
        /** @type {any} */ (window).thahebCart?.bagError?.();
        /** @type {any} */ (window).thahebCart?.refresh?.().catch(() => {});
      }
      return;
    }

    if (this.#addingTimer) clearTimeout(this.#addingTimer);
    this.#addingTimer = setTimeout(() => {
      if (this.refs.dialog?.querySelector('[data-thaheb-adding-row]')) {
        /** @type {any} */ (window).thahebCart?.refresh?.();
        this.#addingTimer = setTimeout(() => this.#endAdding(false), 4000);
      } else {
        this.#endAdding(false);
      }
    }, 2500);
  };

  /**
   * @param {boolean} closeIfOpenedEarly
   */
  #endAdding(closeIfOpenedEarly) {
    if (this.#addingTimer) clearTimeout(this.#addingTimer);
    this.#addingTimer = null;

    const wasAdding = this.#adding;
    const openedEarly = this.#openedEarly;
    this.#adding = false;
    this.#openedEarly = false;

    const dialog = this.refs.dialog;
    dialog?.querySelectorAll('[data-thaheb-adding-row]').forEach((row) => row.remove());
    dialog?.removeAttribute('data-thaheb-adding');

    if (wasAdding && closeIfOpenedEarly && openedEarly && dialog?.open) {
      this.closeDialog();
    }
  }

  /**
   * Puts a "adding to your cart" row (product picture and name when they can be found) at the top of the items.
   * @param {Element | undefined} source
   */
  #showAddingRow(source) {
    const dialog = this.refs.dialog;
    const host = dialog?.querySelector('.cart-drawer__items');
    if (!dialog || !host) return;

    dialog.querySelectorAll('[data-thaheb-adding-row]').forEach((row) => row.remove());
    dialog.setAttribute('data-thaheb-adding', '');

    const { image, title } = this.#readSource(source);

    const row = document.createElement('div');
    row.className = 'thaheb-adding';
    row.setAttribute('data-thaheb-adding-row', '');
    row.setAttribute('role', 'status');

    const media = document.createElement('div');
    media.className = 'thaheb-adding__media';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.decoding = 'async';
      media.append(img);
    }

    const body = document.createElement('div');
    body.className = 'thaheb-adding__body';

    const name = document.createElement('p');
    name.className = title ? 'thaheb-adding__title' : 'thaheb-adding__title thaheb-adding__title--blank';
    name.textContent = title;

    const note = document.createElement('p');
    note.className = 'thaheb-adding__note';
    note.textContent = 'Adding to your cart';

    body.append(name, note);
    row.append(media, body);
    host.prepend(row);
  }

  /**
   * @param {Element | undefined} source
   * @returns {{ image: string, title: string }}
   */
  #readSource(source) {
    let image = '';
    let title = '';

    try {
      if (source instanceof Element) {
        const scope = source.closest('.shopify-section') || source.closest('main') || document;
        const heading = scope.querySelector('h1');
        title = (heading?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 90);

        const picture = /** @type {HTMLImageElement | null} */ (
          scope.querySelector(
            'media-gallery img, [class*="media-gallery"] img, [class*="product-media"] img, [class*="gallery"] img'
          )
        );
        const src = picture?.currentSrc || picture?.src || '';
        if (/^(https?:)?\/\//.test(src)) image = src;
      }
    } catch (_) {
      // Nothing found: the row falls back to a neutral shimmer.
    }

    return { image, title };
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart). The drawer
      // opens on an empty cart for a first add and this only runs on open, so "false" here pushed the footer into the
      // scroll area on short phones: checkout button faded, upsell buttons out of reach.
      dialog.setAttribute('cart-summary-sticky', 'true');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
